import { LinkedinIcon } from '@hedepy/icons/refactor/Linkedin'
import { LogoBusinessHorizontalIcon } from '@hedepy/icons/refactor/LogoBusinessHorizontal'
import type { Translate } from 'next-translate'
import Link from 'next/link'
import React from 'react'

import { ButtonLink } from '@/legacy-src/components/ui/button'
import { cn } from '@/legacy-src/lib/utils'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import AppButton from '../AppButton'
import HomepageButton from '../button/HomepageButton'
import Icon from '../icons/Icon'
import LanguagePicker from '../languagePicker/LanguagePicker'

export interface ContactInfoProps {
  isBusinessPage?: boolean
}

const wrapTranslate = (t: Translate) => (key: string) => {
  const val = t(key)

  if (val === key) {
    return ''
  }

  return val
}

const useGetLink = (isBusinessPage?: boolean) => {
  const t = wrapTranslate(getTranslate().t)

  return (linkKey: string, fallback?: string) => {
    return (
      (isBusinessPage ? t(`component.footer.business.${linkKey}Link`) : null) ||
      t(`component.footer.${linkKey}Link`) ||
      fallback
    )
  }
}

const ContactInfoLogo = ({ isBusinessPage }: ContactInfoProps) => {
  const className = cn('w-full max-w-[190px] p-0 text-white')

  return isBusinessPage ? (
    <Link href="/business">
      <LogoBusinessHorizontalIcon size="auto" className={className} />
    </Link>
  ) : (
    <HomepageButton className={className} />
  )
}

const ContactInfo = ({ isBusinessPage }: ContactInfoProps) => {
  const getLink = useGetLink(isBusinessPage)

  const mail = getLink('mail', process.env.NEXT_PUBLIC_SUPPORT_EMAIL)

  const socialLinks = isBusinessPage
    ? ['facebook', 'instagram', 'youtube', 'linkedin']
    : ['facebook', 'instagram', 'youtube']

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-4">
        <ContactInfoLogo isBusinessPage={isBusinessPage} />
        <LanguagePicker theme="dark" align="end" />
      </div>

      <ButtonLink
        href={`mailto:${mail}`}
        variant="inline"
        className="mb-4 flex w-full items-center justify-between rounded-2xl border-2 border-solid border-petrol-400 !no-underline hover:bg-petrol-400"
      >
        <div className="my-4 ml-5 flex items-center">
          <Icon icon="email" size="sm" className="mr-2 text-pink-500" />
          <div className="font-bold uppercase text-white">{mail}</div>
        </div>
        <Icon icon="up" color="primary" className="mr-5 rotate-45" />
      </ButtonLink>

      <div className="mb-10 mt-10 flex justify-center gap-5 big-tablet:justify-start">
        <AppButton type={'apple'} className="max-h-[40px]" />
        <AppButton type={'google'} className="max-h-[40px]" />
      </div>
      <div className="mb-12 flex justify-center big-tablet:justify-start">
        {socialLinks.map((link) => {
          const href = getLink(link)

          if (!href) {
            return null
          }

          return (
            <Link key={link} href={href} target="_blank" rel="noopener noreferrer" className="mx-2">
              <div className="flex h-[60px] w-[60px] items-center justify-center rounded-2xl border-2 border-solid border-petrol-400 text-pink-500 hover:border-peach-400 hover:text-peach-400">
                {link === 'linkedin' ? <LinkedinIcon size={22} /> : <Icon icon={link} size="sm" className="" />}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export default ContactInfo
